import { View, Text, Image, StyleSheet } from "@react-pdf/renderer";
import { palette } from "./styles";

interface CoverPageProps {
  clientLogoDataUrl: string | null;
  title: string;
  generatedDate: Date;
  kpis: {
    approved: number;
    needs_change: number;
    rejected: number;
    pending: number;
    total: number;
  };
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    flexDirection: "column",
    justifyContent: "space-between",
  },
  headerArea: {
    flexDirection: "column",
    alignItems: "center",
  },
  logo: {
    height: 50,
    maxWidth: 200,
    objectFit: "contain",
    marginBottom: palette.spacing.xl,
  },
  titleBlock: {
    flexDirection: "column",
    alignItems: "center",
    marginTop: palette.spacing.xl * 2,
  },
  title: {
    fontSize: 36,
    fontFamily: "Helvetica-Bold",
    color: palette.text,
    letterSpacing: -0.5,
    textAlign: "center",
  },
  generatedOn: {
    marginTop: palette.spacing.md,
    fontSize: palette.type.small,
    color: palette.muted,
  },
  kpiGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: palette.spacing.xl * 2,
    marginHorizontal: -palette.spacing.sm,
  },
  kpiCell: {
    width: "50%",
    paddingHorizontal: palette.spacing.sm,
    paddingVertical: palette.spacing.sm,
  },
  kpiTile: {
    backgroundColor: palette.softBg,
    borderRadius: 8,
    padding: palette.spacing.lg,
    flexDirection: "column",
    alignItems: "flex-start",
    borderLeftWidth: 3,
    borderLeftStyle: "solid",
  },
  kpiNumber: {
    fontSize: 32,
    fontFamily: "Helvetica-Bold",
  },
  kpiLabel: {
    marginTop: palette.spacing.xs,
    fontSize: palette.type.small,
    color: palette.muted,
    textTransform: "uppercase",
    letterSpacing: 1,
  },
  footer: {
    flexDirection: "column",
    alignItems: "center",
  },
  preparedBy: {
    fontSize: palette.type.small,
    color: palette.muted,
    fontStyle: "italic",
  },
});

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function KpiTile({
  number,
  label,
  accent,
}: {
  number: number;
  label: string;
  accent: string;
}) {
  return (
    <View style={styles.kpiCell}>
      <View style={[styles.kpiTile, { borderLeftColor: accent }]}>
        <Text style={[styles.kpiNumber, { color: accent }]}>{number}</Text>
        <Text style={styles.kpiLabel}>{label}</Text>
      </View>
    </View>
  );
}

export function CoverPage({
  clientLogoDataUrl,
  title,
  generatedDate,
  kpis,
}: CoverPageProps) {
  return (
    <View style={styles.root}>
      <View style={styles.headerArea}>
        {clientLogoDataUrl ? (
          <Image src={clientLogoDataUrl} style={styles.logo} />
        ) : null}
        <View style={styles.titleBlock}>
          <Text style={styles.title}>{title}</Text>
          <Text style={styles.generatedOn}>
            Generated on {formatDate(generatedDate)}
          </Text>
        </View>

        <View style={styles.kpiGrid}>
          <KpiTile
            number={kpis.approved}
            label="Approved"
            accent={palette.status.approved.solid}
          />
          <KpiTile
            number={kpis.needs_change}
            label="Needs change"
            accent={palette.status.needs_change.solid}
          />
          <KpiTile
            number={kpis.rejected}
            label="Rejected"
            accent={palette.status.rejected.solid}
          />
          <KpiTile
            number={kpis.pending}
            label="Pending"
            accent={palette.status.pending.solid}
          />
        </View>
      </View>

      <View style={styles.footer}>
        <Text style={styles.preparedBy}>Prepared by Equip Sport</Text>
      </View>
    </View>
  );
}
